/**
 * Side info drawer (size chart, NRO, info callout, etc.).
 * Trigger buttons use data-info-drawer-target, data-info-drawer-content-template, etc.
 */

/**
 * Nested <dialog> elements (e.g. inside quick-add) cannot open reliably; portal to body.
 * Collect dialogs before portaling so they can still be wired afterward.
 *
 * @param {ParentNode} root
 * @returns {HTMLDialogElement[]}
 */
function collectAndPrepareInfoDrawerDialogs(root = document) {
  const dialogs = Array.from(root.querySelectorAll('[data-info-drawer]')).filter(
    (dialog) => dialog instanceof HTMLDialogElement
  );

  dialogs.forEach((dialog) => {
    const id = dialog.id;
    if (id) {
      document.querySelectorAll(`#${CSS.escape(id)}`).forEach((el) => {
        if (el !== dialog) el.remove();
      });
    }

    const parentDialog = dialog.parentElement?.closest('dialog');
    if (parentDialog && parentDialog !== dialog) {
      document.body.appendChild(dialog);
    }
  });

  return dialogs;
}

/**
 * @param {HTMLDialogElement} dialog
 */
function wireInfoDrawerDialog(dialog) {
  if (dialog.dataset.infoDrawerDialogWired) return;
  dialog.dataset.infoDrawerDialogWired = 'true';

  const inner = dialog.querySelector('.info-drawer__inner');
  let closeTimer = null;

  function openDrawer() {
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
    dialog.showModal();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        dialog.classList.add('info-drawer--open');
      });
    });
  }

  function closeDrawer() {
    dialog.classList.remove('info-drawer--open');
    const duration =
      parseFloat(getComputedStyle(dialog).getPropertyValue('--info-drawer-duration') || '0.4') * 1000;
    closeTimer = setTimeout(() => {
      dialog.close();
      closeTimer = null;
    }, duration);
  }

  dialog.querySelectorAll('[data-info-drawer-close]').forEach((closeBtn) => {
    closeBtn.addEventListener('click', closeDrawer);
  });

  dialog.addEventListener('click', (e) => {
    if (inner && !inner.contains(e.target)) closeDrawer();
  });

  dialog.__openInfoDrawer = openDrawer;
}

/**
 * @param {HTMLElement} btn
 */
function openInfoDrawerFromTrigger(btn) {
  const dialogId = btn.getAttribute('data-info-drawer-target');
  const dialog = dialogId ? document.getElementById(dialogId) : null;
  if (!(dialog instanceof HTMLDialogElement)) return;

  wireInfoDrawerDialog(dialog);

  const contentTemplateId = btn.getAttribute('data-info-drawer-content-template');
  const contentTemplate = contentTemplateId ? document.getElementById(contentTemplateId) : null;
  const contentHost = dialog.querySelector('[data-info-drawer-content]');
  if (contentHost) {
    contentHost.innerHTML = contentTemplate ? contentTemplate.innerHTML : '';
  }

  const previousTypeClass = dialog.dataset.infoDrawerActiveClass;
  if (previousTypeClass) {
    previousTypeClass.split(' ').forEach((className) => {
      if (className) dialog.classList.remove(className);
    });
  }
  const nextTypeClass = btn.getAttribute('data-info-drawer-class') || '';
  if (nextTypeClass) {
    nextTypeClass.split(' ').forEach((className) => {
      if (className) dialog.classList.add(className);
    });
  }
  dialog.dataset.infoDrawerActiveClass = nextTypeClass;

  const ariaLabel = btn.getAttribute('data-info-drawer-aria-label');
  if (ariaLabel) {
    dialog.setAttribute('aria-label', ariaLabel);
  }

  if (typeof dialog.__openInfoDrawer === 'function') {
    dialog.__openInfoDrawer();
  }
}

let infoDrawerClickDelegationWired = false;

function wireInfoDrawerClickDelegation() {
  if (infoDrawerClickDelegationWired) return;
  infoDrawerClickDelegationWired = true;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const btn = target.closest('[data-info-drawer-target]');
      if (!(btn instanceof HTMLElement)) return;

      event.preventDefault();
      event.stopPropagation();
      openInfoDrawerFromTrigger(btn);
    },
    true
  );
}

function wireInfoDrawers(root = document) {
  const dialogs = collectAndPrepareInfoDrawerDialogs(root);
  dialogs.forEach(wireInfoDrawerDialog);
}

/**
 * @param {HTMLElement} element
 */
function observeDynamicInfoDrawerContent(element) {
  if (element.dataset.infoDrawerMutationWired) return;
  element.dataset.infoDrawerMutationWired = 'true';

  const mo = new MutationObserver(() => {
    wireInfoDrawers(element);
  });
  mo.observe(element, { childList: true, subtree: true });
}

function initInfoDrawers() {
  wireInfoDrawerClickDelegation();
  wireInfoDrawers();

  document.querySelectorAll('variant-picker').forEach((vp) => {
    observeDynamicInfoDrawerContent(vp);
  });

  const quickAddContent = document.getElementById('quick-add-modal-content');
  if (quickAddContent instanceof HTMLElement) {
    observeDynamicInfoDrawerContent(quickAddContent);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInfoDrawers);
} else {
  initInfoDrawers();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    wireInfoDrawers(event.target);
  } else {
    wireInfoDrawers();
  }
});

document.addEventListener('theme:dynamic-content', (event) => {
  const customEvent = /** @type {any} */ (event);
  const scope =
    customEvent?.detail?.scope instanceof Element || customEvent?.detail?.scope instanceof Document
      ? customEvent.detail.scope
      : document;
  wireInfoDrawers(scope);
});
